import {
  AppstoreAddOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  GithubOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Empty, Input, message, Modal, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CdManagedTarget, CdReleasePlan, CdReleaseSource, cdDemoApi } from './cdDemoApi';

const { Option } = Select;

type ReleaseHealth = 'healthy' | 'update' | 'failed' | 'deploying' | 'drift' | 'unavailable';

interface ReleasePlanRow {
  id: string;
  targetApp: string;
  targetFile: string;
  environment: CdReleasePlan['environment'];
  sourceProject: string;
  repository: string;
  branch: string;
  currentRelease: string;
  desiredRelease: string;
  health: ReleaseHealth;
  statusDetail: string;
  lastDeployment: string;
}

const healthContent: Record<ReleaseHealth, { label: string; icon: React.ReactNode; action: string }> = {
  healthy: { label: 'Up to date', icon: <CheckCircleOutlined />, action: 'Open overview' },
  update: { label: 'Update available', icon: <WarningOutlined />, action: 'Review release' },
  failed: { label: 'Needs attention', icon: <CloseCircleOutlined />, action: 'Investigate' },
  deploying: { label: 'Deploying', icon: <ClockCircleOutlined />, action: 'Track deployment' },
  drift: { label: 'Configuration drift', icon: <WarningOutlined />, action: 'Investigate drift' },
  unavailable: { label: 'Target unavailable', icon: <CloseCircleOutlined />, action: 'Check target' },
};

function toRow(plan: CdReleasePlan): ReleasePlanRow {
  const latest = plan.latestDeployment;
  const hasUpdate = plan.currentRevision !== plan.desiredRevision;
  const health: ReleaseHealth = !plan.targetAppId
    ? 'failed'
    : latest?.status === 'queued' || latest?.status === 'running'
    ? 'deploying'
    : plan.targetHealth?.status === 'drifted'
      ? 'drift'
      : plan.targetHealth?.status === 'unavailable'
        ? 'unavailable'
    : latest?.status === 'failed'
      ? 'failed'
      : hasUpdate ? 'update' : 'healthy';
  return {
    id: plan.id,
    targetApp: plan.targetAppName,
    targetFile: plan.targetAppId ? `Managed App · ${plan.targetAppId.slice(0, 8)}…` : 'Managed App · target not bound',
    environment: plan.environment,
    sourceProject: plan.sourceAppName ?? 'Consumer Sales',
    repository: plan.repository,
    branch: plan.branch,
    currentRelease: plan.currentVersion ?? 'Not deployed',
    desiredRelease: plan.desiredVersion,
    health,
    statusDetail: !plan.targetAppId
      ? 'Bind a real managed target before deployment'
      : plan.targetHealth?.status === 'drifted'
        ? plan.targetHealth.message
        : plan.targetHealth?.status === 'unavailable'
          ? 'Live Qlik readback could not be confirmed'
      : latest?.status === 'rolled_back'
      ? `Rolled back to ${plan.currentVersion}; ${plan.desiredVersion} remains approved`
      : latest?.status === 'failed'
        ? 'Last deployment needs attention'
        : latest?.status === 'queued' || latest?.status === 'running'
          ? `${latest.version} is moving through the controlled pipeline`
          : hasUpdate ? 'Approved release ready' : 'Target is up to date',
    lastDeployment: latest ? new Date(latest.createdAt).toLocaleString() : 'No deployments yet',
  };
}

export const CdAppsPage: React.FC = () => {
  const history = useHistory();
  const [plans, setPlans] = useState<ReleasePlanRow[]>([]);
  const [targets, setTargets] = useState<CdManagedTarget[]>([]);
  const [search, setSearch] = useState('');
  const [environment, setEnvironment] = useState('all');
  const [health, setHealth] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [targetChoice, setTargetChoice] = useState<string>('');
  const [sources, setSources] = useState<CdReleaseSource[]>([]);
  const [sourceChoice, setSourceChoice] = useState<string>('');
  const [sourceRevision, setSourceRevision] = useState<string>('');
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState('');
  const [targetEnvironment, setTargetEnvironment] = useState<CdReleasePlan['environment']>('Staging');
  const [dataPolicy, setDataPolicy] = useState<CdReleasePlan['dataPolicy']>('reload-target-connections');
  const [refreshing, setRefreshing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  const refreshPlans = useCallback(async () => {
    setRefreshing(true);
    try {
      const [live, targetResult] = await Promise.all([cdDemoApi.listPlans(), cdDemoApi.listTargets()]);
      if (!mounted.current) return;
      setPlans(live.plans.map(toRow));
      setTargets(targetResult.targets);
      setError('');
    } catch (refreshError) {
      if (mounted.current) setError(refreshError instanceof Error ? refreshError.message : 'Release plans could not be loaded.');
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refreshPlans();
    return () => { mounted.current = false; };
  }, [refreshPlans]);

  const visiblePlans = useMemo(() => plans.filter(plan => {
    const term = search.toLowerCase();
    const matchesSearch = plan.targetApp.toLowerCase().includes(term) || plan.sourceProject.toLowerCase().includes(term);
    return matchesSearch && (environment === 'all' || plan.environment === environment) && (health === 'all' || plan.health === health);
  }), [environment, health, plans, search]);
  const availableTargets = useMemo(() => targets.filter(target => !target.releasePlanId), [targets]);
  const selectedSource = useMemo(() => sources.find(source => source.sourceAppId === sourceChoice), [sourceChoice, sources]);

  const openPlanModal = useCallback(async () => {
    setTargetChoice(availableTargets[0]?.appId ?? '');
    setModalOpen(true);
    setSourceLoading(true);
    setSourceError('');
    try {
      const result = await cdDemoApi.listSources();
      if (!mounted.current) return;
      setSources(result.sources);
      const first = result.sources.find(source => source.status === 'available');
      setSourceChoice(first?.sourceAppId ?? '');
      setSourceRevision(first?.approvedRevision ?? '');
    } catch (loadError) {
      if (mounted.current) setSourceError(loadError instanceof Error ? loadError.message : 'Version Control sources could not be loaded.');
    } finally {
      if (mounted.current) setSourceLoading(false);
    }
  }, [availableTargets]);

  const addPlan = async () => {
    const existingTarget = availableTargets.find(target => target.appId === targetChoice);
    if (!existingTarget || !selectedSource || selectedSource.status !== 'available' || selectedSource.approvedRevision !== sourceRevision) return;
    setSaving(true);
    try {
      const plan = await cdDemoApi.createPlan({
        targetAppId: existingTarget.appId,
        targetAppName: existingTarget.name,
        sourceAppId: selectedSource.sourceAppId,
        sourceRevision,
        environment: targetEnvironment,
        dataPolicy,
      });
      setModalOpen(false);
      message.success(`Release plan created for ${plan.targetAppName}.`);
      history.push(`/cd/apps/${plan.id}`);
    } catch (saveError) {
      message.error(saveError instanceof Error ? saveError.message : 'Release plan could not be created.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="release-center-page">
      <div className="page-heading release-center-heading">
        <div><h1>Managed target Apps</h1><p>Monitor the Apps that have a release plan, their live version, and the next controlled deployment action.</p></div>
        <Button aria-label="Add release plan" type="primary" icon={<AppstoreAddOutlined />} onClick={() => void openPlanModal()}>Add release plan</Button>
      </div>

      <section className="release-overview-strip" aria-label="Release portfolio summary">
        <div><DeploymentUnitOutlined /><span><strong>{plans.length}</strong><small>Managed targets</small></span></div>
        <div className="is-success"><CheckCircleOutlined /><span><strong>{plans.filter(plan => plan.health === 'healthy').length}</strong><small>Up to date</small></span></div>
        <div className="is-warning"><WarningOutlined /><span><strong>{plans.filter(plan => plan.health === 'update').length}</strong><small>Release ready</small></span></div>
        <div className="is-danger"><CloseCircleOutlined /><span><strong>{plans.filter(plan => ['failed', 'drift', 'unavailable'].includes(plan.health)).length}</strong><small>Needs attention</small></span></div>
      </section>

      <div className="release-toolbar">
        <Input aria-label="Search release plans" placeholder="Search target App or release source..." prefix={<SearchOutlined />} value={search} onChange={event => setSearch(event.target.value)} />
        <Select aria-label="Environment" value={environment} onChange={setEnvironment}><Option value="all">All environments</Option><Option value="Production">Production</Option><Option value="Staging">Staging</Option><Option value="QA">QA</Option></Select>
        <Select aria-label="Deployment health" value={health} onChange={setHealth}><Option value="all">Deployment health</Option><Option value="healthy">Up to date</Option><Option value="update">Update available</Option><Option value="drift">Configuration drift</Option><Option value="unavailable">Target unavailable</Option><Option value="failed">Deployment failed</Option></Select>
        <Button aria-label="Refresh release status" loading={refreshing} icon={<ReloadOutlined />} onClick={() => void refreshPlans().then(() => message.success('Release status refreshed.'))} />
      </div>

      {error && <Alert type="error" showIcon message="Release Center is unavailable" description={error} action={<Button size="small" onClick={() => void refreshPlans()}>Retry</Button>} />}
      {refreshing && plans.length === 0 ? <div className="release-loading"><Spin /><span>Reading release plans and managed targets…</span></div> : (
        <div className="release-plans-table" role="table" aria-label="Managed target Apps">
          <div className="release-plan-head" role="row"><span>Target managed App</span><span>Environment</span><span>Release source</span><span>Current / desired</span><span>Deployment health</span><span>Last deployment</span><span>Next step</span></div>
          {visiblePlans.map(plan => {
            const healthState = healthContent[plan.health];
            const targetPath = `/cd/apps/${plan.id}`;
            return <div className="release-plan-row" role="row" key={plan.id}>
              <div className="release-target-cell"><span className="app-glyph"><BarChartOutlined /></span><div><Link className="app-name" to={targetPath}>{plan.targetApp}</Link><span className="muted">{plan.targetFile}</span></div></div>
              <span className={`environment-badge is-${plan.environment.toLowerCase()}`}>{plan.environment}</span>
              <div className="release-source-cell"><strong>{plan.sourceProject}</strong><span><GithubOutlined /> {plan.repository}</span><span>{plan.branch}</span></div>
              <div className="release-version-cell"><span><small>Live</small><strong>{plan.currentRelease}</strong></span><i>→</i><span><small>Desired</small><strong>{plan.desiredRelease}</strong></span></div>
              <div className={`release-health is-${plan.health}`}><span>{healthState.icon}{healthState.label}</span><small>{plan.statusDetail}</small></div>
              <div><span>{plan.lastDeployment}</span><span className="muted">by Click-CI</span></div>
              <div className="row-actions"><Link className="ant-btn" to={plan.health === 'failed' || plan.health === 'deploying' ? `${targetPath}/deployments` : targetPath}>{healthState.action}</Link></div>
            </div>;
          })}
          {!visiblePlans.length && !error && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No release plans match the current filters." />}
        </div>
      )}
      <p className="apps-status-note"><DatabaseOutlined /> Only target Apps with a persisted release plan appear here. Development Apps remain in Version Control until a deployment target is explicitly configured.</p>

      <Modal className="release-plan-modal" width={720} title="Add release plan" open={modalOpen} okText="Create release plan" confirmLoading={saving} okButtonProps={{ disabled: !targetChoice || !sourceChoice || !sourceRevision || selectedSource?.status !== 'available' }} onOk={() => void addPlan()} onCancel={() => setModalOpen(false)}>
        <div className="release-plan-intro"><RocketOutlined /><div><strong>Start with an existing managed target</strong><p>The plan is stored in PostgreSQL and governs one real target App. Click-CI never invents a deployment destination.</p></div></div>
        {!availableTargets.length && <Alert type="info" showIcon message="No unplanned managed targets are available" description={<span>Create the target App in the Qlik managed space, then refresh Release Center. This tenant exposes republish for existing targets but does not allow API-first managed App creation. <a href="https://psdemo.eu.qlikcloud.com/hub/catalog" target="_blank" rel="noreferrer">Open Qlik Cloud</a></span>} />}
        {sourceError && <Alert type="error" showIcon message="Version Control sources are unavailable" description={sourceError} action={<Button size="small" onClick={() => void openPlanModal()}>Retry</Button>} />}
        <div className="release-plan-form">
          <label><span><b>1</b><strong>Target managed App</strong><small>Select the real App that business users open</small></span><Select aria-label="Target managed App" placeholder="Select an unplanned target" value={targetChoice || undefined} onChange={setTargetChoice}>{availableTargets.map(target => <Option key={target.appId} value={target.appId}>{target.name}</Option>)}</Select></label>
          <label><span><b>2</b><strong>Environment</strong><small>Controls the release context shown to operators</small></span><Select aria-label="Target environment" value={targetEnvironment} onChange={setTargetEnvironment}><Option value="QA">QA</Option><Option value="Staging">Staging</Option><Option value="Production">Production</Option></Select></label>
          <label><span><b>3</b><strong>Version Control source</strong><small>Select a real App with a reviewed Git mapping</small></span><Select aria-label="Version Control source" loading={sourceLoading} placeholder="Select a verified source" value={sourceChoice || undefined} onChange={value => { const source = sources.find(item => item.sourceAppId === value); setSourceChoice(value); setSourceRevision(source?.approvedRevision ?? ''); }}>{sources.map(source => <Option key={source.sourceAppId} value={source.sourceAppId} disabled={source.status !== 'available'}>{source.sourceAppName} · {source.repository}/{source.branch}</Option>)}</Select></label>
          <label><span><b>4</b><strong>Verified source revision</strong><small>Frozen by the server when this plan is created</small></span><Select aria-label="Verified source revision" loading={sourceLoading} placeholder="Select a source first" value={sourceRevision || undefined} onChange={setSourceRevision} disabled={!selectedSource || selectedSource.status !== 'available'}>{selectedSource?.approvedRevision && <Option value={selectedSource.approvedRevision}>{selectedSource.approvedRevision.slice(0, 7)} · Git HEAD</Option>}</Select></label>
          {selectedSource && <div className={`release-source-proof is-${selectedSource.status}`}><CheckCircleOutlined /><div><strong>{selectedSource.status === 'available' ? 'Server-verified release source' : 'Source unavailable'}</strong><span>{selectedSource.message}</span><small><GithubOutlined /> {selectedSource.repository}/{selectedSource.branch} · mapped {selectedSource.mappedRevision ? selectedSource.mappedRevision.slice(0, 7) : 'none'}</small></div></div>}
          <label><span><b>5</b><strong>Data policy</strong><small>Executed by the server for every deployment</small></span><Select aria-label="Release data policy" value={dataPolicy} onChange={setDataPolicy}><Option value="reload-target-connections">Reload with target connections</Option><Option value="structure-only">Use sealed QVF data · skip reload</Option></Select></label>
        </div>
      </Modal>
    </section>
  );
};
